import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import {
  acceptWorkspaceInvite,
  declineWorkspaceInvite,
  switchWorkspace,
  getWorkspaces,
  getWorkspaceInvites,
  clearUnauthorized,
  joinWorkspaceViaLink,
} from '../../../actions';
import { FormattedMessage, useIntl } from 'react-intl';
import { notification } from 'antd';
import { logoutAction } from '../../../redux/general/actions';
import AuthLayout from './AuthLayout';
import { getWorkspacePalette } from '../../../js/helper';

const WorkspaceAvatar = ({ title }) => {
  const initials = title.slice(0, 2).toUpperCase();
  const palette = getWorkspacePalette(title);
  return (
    <div
      className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 tracking-wide"
      style={{ background: palette.bg, border: `1px solid ${palette.border}`, color: palette.text, letterSpacing: '0.04em' }}
    >
      {initials}
    </div>
  );
};

const SelectWorkspace = (props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout: auth0Logout } = useAuth0();

  const { user, currentWorkspace } = props;

  const [workspaces, setWorkspaces] = useState(props.workspaces);
  const [pendingInvites, setPendingInvites] = useState(props.pendingInvites);

  useEffect(() => {
    // Signed in while connecting an AI app (OAuth consent page): go back to it.
    const pendingOAuthRequestId = localStorage.getItem('pendingOAuthRequestId');
    if (pendingOAuthRequestId && localStorage.getItem('token')) {
      localStorage.removeItem('pendingOAuthRequestId');
      navigate(`/oauth/consent?request_id=${encodeURIComponent(pendingOAuthRequestId)}`, { replace: true });
      return;
    }

    if (location.state?.joinedWorkspaceName) {
      notification.success({
        message: `You've been added to ${location.state.joinedWorkspaceName}`,
        placement: 'bottomRight',
      });
    }

    const pendingToken = localStorage.getItem('pendingInviteToken');
    if (pendingToken) {
      dispatch(joinWorkspaceViaLink(pendingToken, (err, data) => {
        localStorage.removeItem('pendingInviteToken');
        if (!err && data?.workspaceId) {
          localStorage.setItem('workspaceId', data.workspaceId);
          sessionStorage.setItem('workspaceId', data.workspaceId);
          notification.success({ message: `You've been added to ${data.workspaceName}`, placement: 'bottomRight' });
        }
        dispatch(getWorkspaces('lean'));
        dispatch(getWorkspaceInvites());
        dispatch(clearUnauthorized());
      }));
    } else {
      dispatch(getWorkspaces('lean'));
      dispatch(getWorkspaceInvites());
      dispatch(clearUnauthorized());
    }
  }, []);

  useEffect(() => {
    setPendingInvites(props.pendingInvites);
    setWorkspaces(props.workspaces);
  }, [props.pendingInvites, props.workspaces]);

  const setWorkspace = async (workspace) => {
    if (!workspace) return;
    sessionStorage.setItem('workspaceId', workspace.targetId || workspace._id);
    localStorage.setItem('workspaceId', workspace.targetId || workspace._id);
    sessionStorage.setItem('organization', workspace?.organization?._id || workspace?.organization);
    localStorage.setItem('organization', workspace?.organization?._id || workspace?.organization);
    await dispatch(switchWorkspace(workspace, navigate('/', { replace: true })));
  };

  const handleAcceptWorkspace = async (workspace) => {
    await dispatch(acceptWorkspaceInvite(workspace._id, () => {
      setWorkspaces(prev => [...prev, workspace]);
      dispatch(switchWorkspace(workspace, navigate('/', { replace: true })));
    }));
  };

  const handleDeclineWorkspace = async (workspace) => {
    await dispatch(declineWorkspaceInvite(workspace._id, () => {
      setPendingInvites(prev => prev.filter(w => w._id !== workspace._id));
    }));
  };

  const handleLogout = () => {
    ['token', 'workspaceId', 'organization', 'toggle', 'inviteUrl', 'Id'].forEach(k => localStorage.removeItem(k));
    ['workspaceId', 'organization'].forEach(k => sessionStorage.removeItem(k));
    dispatch(logoutAction());
    auth0Logout({ logoutParams: { returnTo: `${window.location.origin}/signin` } });
    window.location.href = '/signin';
  };

  const noData = !pendingInvites?.length && !workspaces?.length;
  const firstName = user?.fullName?.split(' ')[0] || 'there';

  return (
    <AuthLayout maxWidth={600} logoClickable>
      {/* Sign out link in top-right — rendered as overlay since AuthLayout owns the header */}
      <style>{`
        .auth-select-layout-signout {
          position: fixed;
          top: 0;
          right: 0;
          z-index: 20;
          padding: 14px 32px;
        }
      `}</style>
      <div className="auth-select-layout-signout">
        <button
          onClick={handleLogout}
          className="text-sm font-medium transition-colors"
          style={{ color: 'rgba(255,255,255,0.45)' }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
        >
          Sign out
        </button>
      </div>

      {/* Greeting */}
      <div className="text-center mb-7">
        <h1 className="text-2xl font-semibold mb-1.5" style={{ color: 'rgba(255,255,255,0.95)' }}>
          Welcome back, {firstName}!
        </h1>
        {noData ? (
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
            <FormattedMessage id="NO_WORKSPACES_BUT_YOU_CAN_CREATE" defaultMessage="You have no workspaces yet. Create one to get started." />
          </p>
        ) : (
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
            <FormattedMessage id="WORKSPACES_FOR" defaultMessage="Workspaces for" />{' '}
            <span className="font-medium" style={{ color: 'rgba(147,197,253,0.9)' }}>{user?.email}</span>
          </p>
        )}
      </div>

      {/* Pending invites */}
      {pendingInvites?.length > 0 && (
        <div className="mb-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider mb-2 px-1" style={{ color: 'rgba(255,255,255,0.3)' }}>
            <FormattedMessage id="WORKSPACE_INVITES" defaultMessage="Pending invites" />
          </p>
          <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
            {pendingInvites.map((workspace, i) => {
              const title = workspace.title || workspace.targetName || '';
              return (
                <div
                  key={workspace._id}
                  className="flex items-center gap-3 px-4 py-3.5"
                  style={i < pendingInvites.length - 1 ? { borderBottom: '1px solid rgba(255,255,255,0.06)' } : {}}
                >
                  <WorkspaceAvatar title={title} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: 'rgba(255,255,255,0.9)' }}>{title}</p>
                    {workspace?.description && (
                      <p className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>{workspace.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleDeclineWorkspace(workspace)}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
                      style={{ color: 'rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
                    >
                      Decline
                    </button>
                    <button
                      onClick={() => handleAcceptWorkspace(workspace)}
                      className="px-3 py-1.5 text-xs font-medium text-white rounded-lg auth-btn-primary"
                    >
                      Accept
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Workspace list */}
      {workspaces?.length > 0 && (
        <div className="mb-4">
          {pendingInvites?.length > 0 && (
            <p className="text-[11px] font-semibold uppercase tracking-wider mb-2 px-1" style={{ color: 'rgba(255,255,255,0.3)' }}>
              Your workspaces
            </p>
          )}
          <div className="rounded-xl overflow-hidden overflow-y-auto" style={{ maxHeight: '300px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
            {workspaces.map((workspace, i) => {
              const title = workspace.title || workspace.targetName || '';
              return (
                <div
                  key={workspace._id}
                  onClick={() => setWorkspace(workspace)}
                  className="flex items-center gap-3 px-4 py-3.5 cursor-pointer transition-colors"
                  style={i < workspaces.length - 1 ? { borderBottom: '1px solid rgba(255,255,255,0.06)' } : {}}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <WorkspaceAvatar title={title} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: 'rgba(255,255,255,0.9)' }}>{title}</p>
                    {workspace?.description && (
                      <p className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>{workspace.description}</p>
                    )}
                  </div>
                  <span className="text-xs font-semibold flex-shrink-0" style={{ color: 'rgba(165,180,252,0.8)' }}>Open →</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create workspace */}
      <button
        onClick={() => navigate('/create-workspace')}
        className="w-full py-2.5 px-4 text-white text-sm font-semibold rounded-xl shadow-sm mb-3 auth-btn-primary"
      >
        <FormattedMessage id="CREATE_WORKSPACE" defaultMessage="Create Workspace" />
      </button>

      {/* Try different email */}
      <button
        onClick={handleLogout}
        className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl transition-colors"
        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)' }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.09)'}
        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
      >
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'rgba(255,255,255,0.35)' }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-sm flex-1 text-left" style={{ color: 'rgba(255,255,255,0.5)' }}>
          <FormattedMessage id="NOT_SEEING_WORKSPACE" defaultMessage="Not seeing your workspace?" />
        </span>
        <span className="text-sm font-medium flex-shrink-0" style={{ color: 'rgba(147,197,253,0.8)' }}>
          <FormattedMessage id="TRY_DIFFERENT_EMAIL" defaultMessage="Try a different email" />
        </span>
      </button>
    </AuthLayout>
  );
};

function mapStateToProps(state) {
  return {
    user: state.userReducer.user,
    workspaces: state.workspaceReducer.getWorkspaces,
    pendingInvites: state.workspaceReducer.pendingWorkspaceInvites,
    currentWorkspace: state.workspaceReducer.currentWorkspace,
  };
}

export default connect(mapStateToProps)(SelectWorkspace);
